import { useState, useEffect, useMemo, useCallback, useSyncExternalStore, Suspense } from 'react'
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useProfile } from '../../hooks/useProfile'
import { useSidebarCollapsed } from '../../hooks/useSidebarCollapsed'
import { useTheme } from '../../hooks/useTheme'
import { useEcranLarge } from '../../hooks/useLargeurEcran'
import { AlertBadgeContext } from '../../contexts/AlertBadgeContext'
import { ClientAfficheContext } from '../../contexts/ClientAfficheContext'
import { apiFetch } from '../../lib/apiClient'
import { selectableAccounts } from '../../lib/accountScope'
import {
  PARAM_CLIENT, PARAM_VUE, VALEUR_VUE_CLIENT,
  avecClient, ecrireDernierClient, estPageClient, lireDernierClient, resoudreClientAffiche,
} from '../../lib/clientAffiche'
import { accesClientRefuse, oublierAccesClientRefuse, refusVautPour, surAccesClientRefuse } from '../../lib/accesClientRefuse'
import { getNavStructure, itemCouvre } from '../../lib/navigation'
import { LIBELLE_ACCES_REFUSE, entreeCouvrante, libellePage, poserLibellePageCourant, titreOnglet } from '../../lib/titrePage'
import { alertesMock } from '../../mocks/alertesMock'
import type { AlertsResponse } from '../../types/alertes'
import { Header } from './Header'
import { Sidebar } from './Sidebar'
import { AccesRefuse } from './AccesRefuse'
import { LienEvitement, ID_CONTENU } from './LienEvitement'
import { SessionPerdueBanner } from '../auth/SessionPerdueBanner'
import { PageAOnglets } from './PageAOnglets'

/** Spec 161 — le contenu d'une page regroupée est le panneau de sa barre d'onglets. */
const ID_PANNEAU_ONGLETS = 'panneau-onglets-page'

const USE_MOCK = import.meta.env['VITE_USE_MOCK'] === 'true'

/** L'adresse du navigateur est-elle celle du rendu ? (chemins comparés décodés : le routeur peut les décoder.) */
function adresseDuNavigateurEst(pathname: string, search: string, hash: string): boolean {
  try {
    return decodeURI(window.location.pathname) === decodeURI(pathname)
      && window.location.search === search && window.location.hash === hash
  } catch {
    return true // chemin indécodable : on ne bloque pas la complétion
  }
}

function computeCriticalBadges(alerts: AlertsResponse['alerts']): Record<string, number> {
  const badges: Record<string, number> = {}
  for (const a of alerts) {
    if (a.severity === 'critical' && a.status === 'active') {
      badges[a.solution] = (badges[a.solution] ?? 0) + 1
    }
  }
  return badges
}

export function AppShell() {
  const { profile } = useProfile()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [sidebarCollapsed, setSidebarCollapsed] = useSidebarCollapsed()
  // Spec 160 (FR-016, FR-017) — sous 1 280 px, la colonne d'icônes s'impose ; la préférence reste mémorisée.
  const ecranLarge = useEcranLarge()
  // Active la gestion du thème (dark mode) au niveau global — applique la classe .dark
  // dès que les préférences utilisateur sont chargées (spec 046 D-DarkMode-01).
  useTheme()
  // Spec 160 (D-160-03) — client et Vue client COURANTS DE L'ONGLET. La coquille ne se démonte pas d'une page à
  // l'autre : ces états suivent l'onglet, et gardent son client quand une navigation interne ne le porte pas. Le
  // « dernier consulté » (navigateur) ne sert qu'à un onglet neuf, sans quoi deux onglets se contamineraient.
  const [clientOnglet, setClientOnglet] = useState<string | null>(null)
  const [vueOnglet, setVueOnglet] = useState(false)
  const [alertBadges, setAlertBadges] = useState<Record<string, number>>({})
  const [activeCount, setActiveCount] = useState<number>(0)
  const [urgentRenewals, setUrgentRenewals] = useState<number>(0)

  // Spec 160 (D-160-02) — le client affiché se décide ICI, une fois, avant tout rendu de page, à partir du seul
  // périmètre de `selectableAccounts` ; les pages, le bandeau et le titre lisent ce résultat et rien d'autre.
  const perimetre = useMemo(() => selectableAccounts(profile), [profile])
  const pageClient = estPageClient(location.pathname)
  const depuisAdresse = searchParams.get(PARAM_CLIENT)
  const resolu = resoudreClientAffiche({
    perimetre,
    depuisAdresse,
    pageClient,
    ongletCourant: clientOnglet,
    dernierConsulte: profile ? lireDernierClient(profile.userId) : null,
  })
  const signalRefus = useSyncExternalStore(surAccesClientRefuse, accesClientRefuse)
  const clientResolu = resolu.kind === 'client' ? resolu.account : null
  // Le refus du serveur ne vaut que pour le client affiché (un 403 tardif d'une autre page n'y change rien).
  const refusServeur = refusVautPour(signalRefus, clientResolu?.accountId ?? null)
  const refuse = resolu.kind === 'refus' || refusServeur
  const selectedAccountId = !refuse && clientResolu ? clientResolu.accountId : null
  // Vue client (D-160-09) : l'adresse la dit sur une page client qui désigne déjà son client ; ailleurs, l'onglet.
  // Sans effet pour un utilisateur Client.
  const vueDemandee = searchParams.get(PARAM_VUE) === VALEUR_VUE_CLIENT
  const clientViewMode = profile?.role !== 'Client' && (pageClient && depuisAdresse !== null ? vueDemandee : vueOnglet)

  const nav = useMemo(() => (profile ? getNavStructure(profile.role, profile.resellerTier) : null), [profile])
  // Spec 161 (D-161-07) — l'entrée de menu qui réunit la page affichée sous des onglets, s'il y en a une (`PageAOnglets`).
  const entreeAOnglets = useMemo(
    () => nav?.sections.flatMap(s => s.items).find(i => (i.onglets?.length ?? 0) > 0 && itemCouvre(i, location.pathname)) ?? null,
    [nav, location.pathname],
  )
  const ici = `${location.pathname}${location.search}${location.hash}`

  // Le client résolu devient celui de l'onglet, et le dernier consulté de cet utilisateur sur ce navigateur.
  useEffect(() => {
    if (!selectedAccountId) return
    setClientOnglet(selectedAccountId)
    if (profile) ecrireDernierClient(profile.userId, selectedAccountId)
  }, [selectedAccountId, profile])

  // Sur une page client qui désigne son client, l'adresse fait foi pour la Vue client de l'onglet.
  useEffect(() => {
    if (pageClient && depuisAdresse !== null && resolu.kind === 'client') setVueOnglet(clientViewMode)
  }, [pageClient, depuisAdresse, resolu.kind, clientViewMode])

  // L'adresse d'une page client DÉSIGNE son client (FR-001, FR-004) : complétée par remplacement — aucune entrée
  // d'historique en plus —, en gardant l'état de navigation (Simulations y reçoit un import en attente). Une page sans
  // client perd ces paramètres. Une adresse refusée reste telle quelle : c'est elle que l'utilisateur doit voir.
  useEffect(() => {
    // Une page vient-elle de rediriger dans ce même rendu (ex. `/licences/<licence>` → `/licences?sku=…`) ? Son
    // `<Navigate replace>` a déjà réécrit l'adresse du navigateur, et le rendu qui suivra la portera : compléter
    // l'adresse d'AVANT l'écraserait, et la page resterait vide (revue à froid du 2026-10-11).
    if (!adresseDuNavigateurEst(location.pathname, location.search, location.hash)) return
    if (!pageClient) {
      if (!searchParams.has(PARAM_CLIENT) && !searchParams.has(PARAM_VUE)) return
      const reste = new URLSearchParams(searchParams)
      reste.delete(PARAM_CLIENT)
      reste.delete(PARAM_VUE)
      const q = reste.toString()
      navigate(`${location.pathname}${q ? `?${q}` : ''}${location.hash}`, { replace: true, state: location.state })
      return
    }
    if (resolu.kind !== 'client') return
    const voulu = avecClient(ici, resolu.account.accountId, clientViewMode)
    if (voulu !== ici) navigate(voulu, { replace: true, state: location.state })
  }, [pageClient, searchParams, resolu, ici, clientViewMode, location.pathname, location.search, location.hash, location.state, navigate])

  // Un refus du serveur vaut pour la page qui l'a reçu : la suivante le relèvera s'il persiste (D-160-06).
  useEffect(() => {
    oublierAccesClientRefuse()
  }, [location.pathname, location.search])

  // Titre d'onglet et libellé de page (D-160-08) — le libellé seul part en télémétrie, jamais le nom du client.
  const libelle = refuse ? LIBELLE_ACCES_REFUSE : libellePage(location.pathname, nav)
  const nomClient = !refuse && pageClient && clientResolu ? clientResolu.displayName : null
  useEffect(() => {
    poserLibellePageCourant(libelle)
    document.title = titreOnglet(libelle, nomClient)
  }, [libelle, nomClient])

  // Lien de l'écran de refus : la même page pour le client par défaut — hors du client que le serveur vient de refuser.
  const lienRetour = useMemo(() => {
    if (!refuse) return null
    const exclu = refusServeur ? clientResolu?.accountId ?? null : null
    const defaut = resoudreClientAffiche({
      perimetre: exclu ? perimetre.filter(a => a.accountId !== exclu) : perimetre,
      depuisAdresse: null,
      pageClient: true,
      ongletCourant: clientOnglet,
      dernierConsulte: profile ? lireDernierClient(profile.userId) : null,
    })
    if (defaut.kind !== 'client') return null
    const chemin = pageClient ? entreeCouvrante(location.pathname, nav)?.chemin ?? '/overview' : '/overview'
    return avecClient(chemin, defaut.account.accountId)
  }, [refuse, refusServeur, clientResolu, perimetre, clientOnglet, profile, pageClient, location.pathname, nav])

  // Changer de client (D-160-10) : l'entrée de menu de la page courante pour le nouveau client, sans le détail ni les
  // paramètres de l'ancien ; une entrée d'historique (Précédent revient à l'ancien client).
  const choisirClient = useCallback((accountId: string) => {
    setVueOnglet(false)
    if (pageClient) {
      const chemin = entreeCouvrante(location.pathname, nav)?.chemin ?? location.pathname
      navigate(avecClient(chemin, accountId))
    } else {
      setClientOnglet(accountId)
      if (profile) ecrireDernierClient(profile.userId, accountId)
    }
  }, [pageClient, location.pathname, nav, navigate, profile])

  const basculerVueClient = useCallback((active: boolean) => {
    setVueOnglet(active)
    if (pageClient && selectedAccountId) navigate(avecClient(ici, selectedAccountId, active), { replace: true, state: location.state })
  }, [pageClient, selectedAccountId, ici, navigate, location.state])

  // Badge alertes critiques — chargé dès la sélection du tenant (avant ouverture d'AlertesPage). Spec 160 : la même
  // réponse donne le nombre d'alertes actives de la cloche — aucune requête de plus.
  useEffect(() => {
    // Remis à zéro au départ de chaque requête, et réponse ignorée si le client a changé entre-temps : sans cela, la
    // cloche gardait le nombre du client précédent (revue à froid de l'incrément 2), comme les pastilles du menu.
    setAlertBadges({})
    setActiveCount(0)
    if (!selectedAccountId) return
    if (USE_MOCK) {
      setAlertBadges(computeCriticalBadges(alertesMock))
      setActiveCount(alertesMock.filter(a => a.status === 'active').length)
      return
    }
    let annule = false
    apiFetch<AlertsResponse>(`/api/alerts?accountId=${selectedAccountId}&status=active`)
      .then(res => {
        if (annule) return
        setAlertBadges(computeCriticalBadges(res.alerts))
        setActiveCount(res.alerts.filter(a => a.status === 'active').length)
      })
      .catch(() => {}) // badge non critique — échec silencieux (la cloche reste à zéro, jamais au nombre d'un autre client)
    return () => { annule = true }
  }, [selectedAccountId])

  /* US2 (073) — fallback du code-splitting confiné à la zone contenu : le shell (sidebar/header) reste affiché pendant
     le chargement d'un chunk de route. */
  const contenuDeLaPage = (
    <Suspense fallback={
      <div className="flex items-center justify-center h-full p-6">
        <p className="text-muted-foreground font-body text-sm">Chargement…</p>
      </div>
    }>
      <Outlet context={{ selectedAccountId, clientViewMode }} />
    </Suspense>
  )

  const clientAffiche = useMemo(
    () => ({ accountId: selectedAccountId, vueClient: clientViewMode }),
    [selectedAccountId, clientViewMode],
  )

  return (
    <ClientAfficheContext.Provider value={clientAffiche}>
    <AlertBadgeContext.Provider value={{ badges: alertBadges, setBadges: setAlertBadges, urgentRenewals, setUrgentRenewals, activeCount, setActiveCount }}>
      <div className="flex h-screen bg-background">
        <LienEvitement />
        <Sidebar
          collapsed={sidebarCollapsed || !ecranLarge}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          peutBasculer={ecranLarge}
        />
        <div className="flex flex-col flex-1 overflow-hidden">
          <Header
            selectedAccountId={selectedAccountId}
            onSelectTenant={choisirClient}
            clientViewMode={clientViewMode}
            onToggleClientView={basculerVueClient}
          />
          {/* Issue #57 — la porte de sortie unique quand la session tombe. Sous le header, donc
              au-dessus de toute page : une session perdue est un événement du shell, pas de la
              page qui a eu la malchance d'appeler l'API en premier. */}
          <SessionPerdueBanner />
          <main id={ID_CONTENU} tabIndex={-1} className="flex-1 overflow-y-auto focus:outline-none">
            {refuse ? (
              // Spec 160 — aucune page montée, donc aucune requête pour un client refusé (FR-005, FR-006).
              <AccesRefuse lienRetour={lienRetour} />
            ) : (
              <>
                {/* Spec 161 (D-161-07) — pages réunies sous une entrée : la page en panneau, sa barre d'onglets sous son
                    titre (`OngletsDeLEntree`), ou en tête si elle ne l'a pas posée. L'enveloppe du panneau n'existe que
                    sous une entrée à onglets : les autres pages gardent leur arbre. */}
                {entreeAOnglets ? (
                  <PageAOnglets entree={entreeAOnglets} idPanneau={ID_PANNEAU_ONGLETS}>{contenuDeLaPage}</PageAOnglets>
                ) : contenuDeLaPage}
              </>
            )}
          </main>
        </div>
      </div>
    </AlertBadgeContext.Provider>
    </ClientAfficheContext.Provider>
  )
}
